/**
 * Canonical vehicle-run reorder legality (shared by API and web UI).
 * API remains authoritative; UI uses these helpers to avoid illegal requests.
 */
export const ELIGIBLE_NEXT_STATUSES = ['dispatched', 'executing', 'on_hold'];
/** Statuses that may not change position in a reorder. */
export const IMMOVABLE_STATUSES = [
    'completed',
    'ready_to_bill',
    'billed',
    'closed',
    'cancelled',
    'declined',
    'failed',
];
/** In-progress statuses: the active trip must stay put and cannot move. */
export const ACTIVE_STATUSES = ['executing', 'on_hold'];
export const TERMINAL_RUN_TRIP_STATUSES = [
    'completed',
    'cancelled',
    'failed',
    'declined',
    'billed',
    'closed',
    'ready_to_bill',
];
export function isEligibleNextTripStatus(status) {
    return ELIGIBLE_NEXT_STATUSES.includes(status);
}
export function isImmovableTripStatus(status) {
    return IMMOVABLE_STATUSES.includes(status);
}
export function isActiveTripStatus(status) {
    return ACTIVE_STATUSES.includes(status);
}
export function isTerminalRunTripStatus(status) {
    return TERMINAL_RUN_TRIP_STATUSES.includes(status);
}
/**
 * First trip later in the same run that crew can execute.
 * Skips cancelled/completed/confirmed/requested — only dispatched/executing/on_hold.
 */
export function findNextEligibleTrip(slots, afterTripId) {
    const current = slots.find((s) => s.tripId === afterTripId);
    if (!current)
        return null;
    const later = slots
        .filter((s) => s.sequence > current.sequence)
        .sort((a, b) => a.sequence - b.sequence);
    return later.find((s) => isEligibleNextTripStatus(s.status)) ?? null;
}
/**
 * Validate a proposed reorder against immovable/active rules.
 * `current` is the existing ordered slots; `orderedTripIds` is the new order (1-based positions).
 *
 * Product rules:
 * - immovable (completed/cancelled/…) positions cannot change sequence
 * - the active trip (executing/on_hold) must remain at its index and cannot move
 * - remaining unstarted trips may reorder only AFTER the active trip
 * - no other trip may receive a newSeq <= the active trip's (old) sequence
 */
export function validateReorderMoves(current, orderedTripIds) {
    const byId = new Map(current.map((s) => [s.tripId, s]));
    const violations = [];
    const actives = current.filter((s) => isActiveTripStatus(s.status));
    const activeFenceSeq = actives.length > 0 ? Math.min(...actives.map((s) => s.sequence)) : null;
    for (let i = 0; i < orderedTripIds.length; i++) {
        const tripId = orderedTripIds[i];
        const slot = byId.get(tripId);
        if (!slot)
            continue;
        const newSeq = i + 1;
        const moved = newSeq !== slot.sequence;
        if (isImmovableTripStatus(slot.status) && moved) {
            violations.push({
                code: 'completed_moved',
                tripId,
                from: slot.sequence,
                to: newSeq,
            });
            continue;
        }
        if (isActiveTripStatus(slot.status) && moved) {
            violations.push({
                code: 'active_moved',
                tripId,
                from: slot.sequence,
                to: newSeq,
            });
            continue;
        }
        if (activeFenceSeq !== null &&
            !isActiveTripStatus(slot.status) &&
            !isImmovableTripStatus(slot.status) &&
            newSeq <= activeFenceSeq) {
            violations.push({
                code: 'jumped_ahead_of_active',
                tripId,
                from: slot.sequence,
                to: newSeq,
            });
        }
    }
    return violations;
}
export function isLegalReorder(current, orderedTripIds) {
    return validateReorderMoves(current, orderedTripIds).length === 0;
}
/** Whether a trip may initiate a drag / be the moving row. */
export function canInitiateReorder(slotStatus) {
    return !isImmovableTripStatus(slotStatus) && !isActiveTripStatus(slotStatus);
}
